import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  CheckCircle,
  Star,
  Bolt,
  Diamond,
  TrendingUp,
  Cloud,
  DevicesOther,
  Security,
  Analytics,
  Close,
  Celebration,
  RocketLaunch,
  Launch,
  LockOpen,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../routes";
import { useAuth } from "../providers/AuthProvider";
import { EnterprisesAPI } from "../api/enterprises";
import { PaymentAPI } from "../api/payment";
import Confetti from "react-confetti";

const plans = {
  free: {
    name: "Free",
    icon: Star,
    description: "Perfect for trying out our platform",
    monthlyPrice: 0,
    yearlyPrice: 0,
    users: 1,
    features: [
      { text: "Account owner only", included: true },
      { text: "No additional users", included: true },
      { text: "Core platform access", included: true },
      { text: "Email support", included: true },
      { text: "Dashboard & Reports", included: true },
      { text: "Cloud integrations", included: false },
      { text: "Advanced analytics", included: false },
      { text: "Priority support", included: false },
      { text: "Custom branding", included: false },
    ],
    color: "#6366f1",
    gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  startup: {
    name: "Startup",
    icon: Bolt,
    description: "For growing teams and businesses",
    monthlyPrice: 1499,
    yearlyPrice: 4999,
    users: 5,
    popular: true,
    features: [
      { text: "Up to 5 named users", included: true },
      { text: "Core inventory and monitoring", included: true },
      { text: "Advanced operational workflows", included: true },
      { text: "Cloud integrations (M365, Azure)", included: true },
      { text: "Real-time monitoring", included: true },
      { text: "Email & chat support", included: true },
      { text: "Advanced analytics", included: true },
      { text: "Bulk import/export", included: true },
      { text: "Custom branding", included: false },
    ],
    color: "#059669",
    gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
  },
  scale: {
    name: "Scale",
    icon: TrendingUp,
    description: "For established enterprises",
    monthlyPrice: 4999,
    yearlyPrice: 14999,
    users: 25,
    features: [
      { text: "Up to 25 named users", included: true },
      { text: "Core inventory and monitoring", included: true },
      { text: "Everything in Startup", included: true },
      { text: "AI-powered insights", included: true },
      { text: "Multi-location support", included: true },
      { text: "Priority support (24/7)", included: true },
      { text: "Custom workflows", included: true },
      { text: "API access", included: true },
      { text: "Custom branding", included: true },
    ],
    color: "#2563eb",
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
  },
  premium: {
    name: "Enterprise",
    icon: Diamond,
    description: "Tailored for large organizations",
    monthlyPrice: null,
    yearlyPrice: null,
    users: "Custom",
    features: [
      { text: "More than 25 named users", included: true },
      { text: "Custom user-seat agreement", included: true },
      { text: "Everything in Scale", included: true },
      { text: "Dedicated account manager", included: true },
      { text: "Custom integrations", included: true },
      { text: "On-premise deployment option", included: true },
      { text: "SLA guarantee", included: true },
      { text: "Training & onboarding", included: true },
      { text: "White-label solution", included: true },
    ],
    color: "#7c3aed",
    gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  },
};

const RECOMMENDED_PLAN = "startup";

const fadeInUp = {
  "@keyframes pageFadeUp": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

const sectionMotion = delay => ({
  ...fadeInUp,
  opacity: 0,
  animation: `pageFadeUp 380ms ease ${delay}ms forwards`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 1,
    transform: "none",
  },
});

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  const [gstin, setGstin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const navigate = useNavigate();
  const auth = useAuth();
  const isLoggedIn = Boolean(auth?.user);
  const isEnterpriseAdmin = auth?.user?.role === "enterprise_admin";

  const openDemoExplorer = () => {
    navigate(ROUTES.DEMO_EXPLORER);
  };

  useEffect(() => {
    if (isEnterpriseAdmin) {
      EnterprisesAPI.getMyEnterprise()
        .then((res) => setEnterpriseInfo(res?.data?.data || null))
        .catch(() => setEnterpriseInfo(null));
    }
  }, [isEnterpriseAdmin]);

  // Countdown and auto-redirect effect
  useEffect(() => {
    let timer;
    if (successDialog && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (successDialog && countdown === 0) {
      navigate(ROUTES.DASHBOARD);
    }
    return () => clearTimeout(timer);
  }, [successDialog, countdown, navigate]);

  const handleGetStarted = (planKey) => {
    if (planKey === "free") {
      navigate(ROUTES.ONBOARDING);
    } else if (planKey === "premium") {
      window.location.href =
        "mailto:sales@sushaservices.com?subject=Premium Plan Inquiry";
    } else {
      if (!isEnterpriseAdmin) {
        navigate("/login");
        return;
      }
      setSelectedPlan(planKey);
      setPaymentDialog(true);
      setError("");
    }
  };

  const calculateGST = (basePrice) => {
    const cgst = Math.round(basePrice * 0.025);
    const sgst = Math.round(basePrice * 0.025);
    return { cgst, sgst, total: basePrice + cgst + sgst };
  };

  const calculateProratedAmount = () => {
    if (!selectedPlan || !enterpriseInfo) return null;

    const currentPlan = enterpriseInfo.user_status || 'free';
    const newPlan = plans[selectedPlan];
    const currentPlanConfig = plans[currentPlan];

    // If upgrading from free, pay full amount
    if (currentPlan === 'free') {
      const basePrice = billingCycle === 'yearly' ? newPlan.yearlyPrice : newPlan.monthlyPrice;
      return {
        isUpgrade: true,
        currentPlanPrice: 0,
        newPlanPrice: basePrice,
        proratedAmount: basePrice,
        daysRemaining: 0,
        isProrated: false,
      };
    }

    // Calculate proration if there's an active subscription
    const currentPlanPrice = billingCycle === 'yearly' ? currentPlanConfig.yearlyPrice : currentPlanConfig.monthlyPrice;
    const newPlanPrice = billingCycle === 'yearly' ? newPlan.yearlyPrice : newPlan.monthlyPrice;
    
    // Check if there's a renewal date for proration
    if (enterpriseInfo.subscription_renewal_date) {
      const now = new Date();
      const renewalDate = new Date(enterpriseInfo.subscription_renewal_date);
      const daysRemaining = Math.max(0, Math.ceil((renewalDate - now) / (1000 * 60 * 60 * 24)));
      const totalDays = billingCycle === 'yearly' ? 365 : 30;
      
      if (daysRemaining > 0) {
        // Calculate unused portion of current plan
        const unusedPortion = (daysRemaining / totalDays);
        const unusedCredit = Math.round(currentPlanPrice * unusedPortion);
        
        // Calculate prorated amount: new plan price - unused credit
        const proratedAmount = Math.max(0, newPlanPrice - unusedCredit);
        
        return {
          isUpgrade: true,
          currentPlanPrice,
          newPlanPrice,
          proratedAmount,
          unusedCredit,
          daysRemaining,
          isProrated: true,
        };
      }
    }

    // No active subscription or renewal date, pay full amount
    return {
      isUpgrade: true,
      currentPlanPrice,
      newPlanPrice,
      proratedAmount: newPlanPrice,
      daysRemaining: 0,
      isProrated: false,
    };
  };

  const handlePayNow = async () => {
    if (!selectedPlan || !enterpriseInfo) return;

    setLoading(true);
    setError("");

    try {
      // Get prorated calculation
      const proratedInfo = calculateProratedAmount();
      
      // Create order
      const orderResponse = await PaymentAPI.createOrder({
        plan: selectedPlan,
        billingCycle: billingCycle,
        gstin: gstin,
        proratedAmount: proratedInfo?.proratedAmount,
        isProrated: proratedInfo?.isProrated,
        unusedCredit: proratedInfo?.unusedCredit,
      });

      const { orderId, amount, key } = orderResponse.data.data;

      // Load Razorpay script
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key: key,
          amount: amount * 100,
          currency: "INR",
          name: "Susha Assets Management",
          description: `${plans[selectedPlan].name} Plan - ${billingCycle}`,
          order_id: orderId,
          prefill: {
            name: enterpriseInfo.poc_name || enterpriseInfo.name,
            email: enterpriseInfo.email || enterpriseInfo.poc_email,
            contact: enterpriseInfo.poc_phone,
          },
          theme: {
            color: plans[selectedPlan].color,
          },
          handler: async function (response) {
            try {
              // Verify payment
              await PaymentAPI.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                plan: selectedPlan,
                billingCycle: billingCycle,
                gstin: gstin,
              });

              setPaymentDialog(false);
              setSuccessDialog(true);
              setShowConfetti(true);
              setCountdown(5);
              setTimeout(() => setShowConfetti(false), 5000);

              // Refresh enterprise info
              const refreshResponse = await EnterprisesAPI.getMyEnterprise();
              setEnterpriseInfo(refreshResponse?.data?.data || null);
            } catch (error) {
              console.error("Payment verification error:", error);
              setError("Payment verification failed. Please contact support.");
            }
          },
          modal: {
            ondismiss: function () {
              setLoading(false);
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
        setLoading(false);
      };

      script.onerror = () => {
        setError("Failed to load payment gateway. Please try again.");
        setLoading(false);
      };
    } catch (error) {
      console.error("Payment error:", error);
      setError(
        error.response?.data?.message ||
          "Failed to initiate payment. Please try again."
      );
      setLoading(false);
    }
  };

  const calculateSavings = (monthly, yearly) => {
    if (!monthly || !yearly) return 0;
    const yearlyMonthly = yearly / 12;
    const savings = ((monthly - yearlyMonthly) / monthly) * 100;
    return Math.round(savings);
  };

  const getPriceBreakdown = () => {
    if (!selectedPlan) return null;
    const proratedInfo = calculateProratedAmount();
    if (!proratedInfo) return null;
    
    const basePrice = proratedInfo.proratedAmount;
    const gst = calculateGST(basePrice);
    
    return {
      ...gst,
      ...proratedInfo,
    };
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        py: { xs: 3, sm: 5, md: 8 },
      }}
    >
      <Container maxWidth="xl" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Header */}
        <Box sx={{ ...sectionMotion(0), textAlign: "center", mb: { xs: 3.2, sm: 4.5, md: 6 }, width: '100%' }}>
          <Chip
            label="PRICING"
            sx={{
              bgcolor: "#e0e7ff",
              color: "#4f46e5",
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              mb: 2,
              fontSize: { xs: "1.65rem", sm: "2.15rem", md: "3rem" },
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto", mb: { xs: 2.3, sm: 3.2, md: 4 }, fontSize: { xs: "0.92rem", sm: "1.05rem" } }}
          >
            Pricing is based only on named users. Asset and device volume is unrestricted on every plan.
          </Typography>

          {/* Billing Toggle */}
          <Paper
            sx={{
              display: "inline-flex",
              p: 0.75,
              bgcolor: "#EEF4FF",
              borderRadius: 2,
              border: "1px solid #BFDBFE",
              boxShadow: "0 8px 20px rgba(5,70,147,0.12)",
              width: { xs: "100%", sm: "auto" },
              maxWidth: { xs: 420, sm: "none" },
            }}
          >
            <Button
              onClick={() => setBillingCycle("monthly")}
              sx={{
                px: { xs: 1.6, sm: 3 },
                py: 1,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: billingCycle === "monthly" ? "white" : "transparent",
                color: billingCycle === "monthly" ? "#054693" : "#64748b",
                boxShadow: billingCycle === "monthly" ? 1 : 0,
                "&:hover": {
                  bgcolor: billingCycle === "monthly" ? "white" : "#e2e8f0",
                },
              }}
            >
              Billed Monthly
            </Button>
            <Button
              onClick={() => setBillingCycle("yearly")}
              sx={{
                px: { xs: 1.6, sm: 3 },
                py: 1,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 600,
                bgcolor: billingCycle === "yearly" ? "white" : "transparent",
                color: billingCycle === "yearly" ? "#054693" : "#64748b",
                boxShadow: billingCycle === "yearly" ? 1 : 0,
                "&:hover": {
                  bgcolor: billingCycle === "yearly" ? "white" : "#e2e8f0",
                },
              }}
            >
              Billed Annually
              <Chip
                label={`Save ${calculateSavings(1499, 4999)}%!`}
                size="small"
                sx={{
                  ml: { xs: 0.6, sm: 1 },
                  bgcolor: "#F97316",
                  color: "white",
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                }}
              />
            </Button>
          </Paper>
        </Box>

        {/* Pricing Cards - All in One Row */}
        <Grid container spacing={{ xs: 1.4, sm: 2.2, md: 3 }} sx={{ ...sectionMotion(90), mb: { xs: 3.4, sm: 4.2, md: 6 }, maxWidth: '1400px', mx: 'auto', justifyContent: 'center' }}>
          {Object.entries(plans).map(([key, plan]) => {
            const PlanIcon = plan.icon;
            const price =
              billingCycle === "yearly" ? plan.yearlyPrice : plan.monthlyPrice;
            const pricePerMonth =
              billingCycle === "yearly" && plan.yearlyPrice
                ? Math.round(plan.yearlyPrice / 12)
                : price;

            const isRecommended = key === RECOMMENDED_PLAN;
            return (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Paper
                  elevation={isRecommended ? 10 : 1}
                  sx={{
                    p: { xs: 2.1, sm: 2.4, md: 3 },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    border: isRecommended
                      ? "2px solid #054693"
                      : "1px solid #e2e8f0",
                    borderRadius: 3,
                    transition: "all 0.3s ease",
                    transform: { xs: "none", md: isRecommended ? "scale(1.03)" : "none" },
                    "&:hover": {
                      transform: { xs: "none", md: "translateY(-8px)" },
                      boxShadow: 6,
                    },
                  }}
                >
                  {isRecommended && (
                    <Chip
                      label="RECOMMENDED"
                      sx={{
                        position: "absolute",
                        top: -12,
                        right: 20,
                        bgcolor: "#054693",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      background: plan.gradient,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <PlanIcon sx={{ fontSize: 32, color: "white" }} />
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}
                  >
                    {plan.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, minHeight: 40 }}
                  >
                    {plan.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {price === null ? (
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 800, color: "#0f172a" }}
                      >
                        Custom
                      </Typography>
                    ) : price === 0 ? (
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 800, color: "#0f172a" }}
                      >
                        Free
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", alignItems: "baseline" }}>
                          <Typography
                            variant="h6"
                            sx={{ color: "#64748b", mr: 0.5 }}
                          >
                            ₹
                          </Typography>
                          <Typography
                            variant="h3"
                            sx={{ fontWeight: 800, color: "#0f172a" }}
                          >
                            {pricePerMonth.toLocaleString("en-IN")}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            /month
                          </Typography>
                        </Box>
                        {billingCycle === "yearly" && (
                          <Typography variant="caption" color="text.secondary">
                            ₹{price.toLocaleString("en-IN")} billed annually
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: plan.color,
                      mb: 2,
                      fontSize: "0.95rem",
                    }}
                  >
                    {key === "free"
                      ? "Account owner only"
                      : typeof plan.users === "number"
                      ? `Up to ${plan.users} named user${plan.users === 1 ? "" : "s"}`
                      : "More than 25 named users"}
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <List dense sx={{ flexGrow: 1, mb: 2 }}>
                    {plan.features.slice(0, 6).map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle
                            sx={{
                              fontSize: 18,
                              color: feature.included
                                ? plan.color
                                : "#cbd5e1",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.text}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              color: feature.included ? "#334155" : "#94a3b8",
                              textDecoration: feature.included
                                ? "none"
                                : "line-through",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    fullWidth
                    variant={isRecommended ? "contained" : "outlined"}
                    size="large"
                    onClick={() => handleGetStarted(key)}
                    disabled={
                      (enterpriseInfo && (
                        enterpriseInfo.user_status === key ||
                        (key === "free" && enterpriseInfo.user_status !== "free") ||
                        (key === "startup" && ["scale", "premium"].includes(enterpriseInfo.user_status)) ||
                        (key === "scale" && enterpriseInfo.user_status === "premium")
                      ))
                    }
                    sx={{
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      ...(isRecommended
                        ? {
                            bgcolor: "#054693",
                            "&:hover": { bgcolor: "#04356F" },
                          }
                        : {
                            borderColor: plan.color,
                            color: plan.color,
                            "&:hover": {
                              borderColor: plan.color,
                              bgcolor: `${plan.color}10`,
                            },
                          }),
                    }}
                  >
                    {enterpriseInfo && enterpriseInfo.user_status === key
                      ? "Your Current Plan"
                      : key === "free"
                      ? "Get Started Free"
                      : key === "premium"
                      ? "Contact Sales"
                      : `Upgrade to ${plan.name}`}
                  </Button>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {!isLoggedIn && (
          <Paper
            variant="outlined"
            sx={{
              ...sectionMotion(130),
              width: "100%",
              maxWidth: "1400px",
              mb: { xs: 3.2, sm: 4.2, md: 5.2 },
              borderRadius: 3,
              p: { xs: 2, sm: 2.8, md: 3 },
              border: "1px dashed #93c5fd",
              background:
                "linear-gradient(135deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", md: "center" },
                gap: { xs: 2, md: 3 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Chip
                  icon={<RocketLaunch />}
                  label="Instant Live Demo"
                  sx={{
                    mb: 1.2,
                    bgcolor: "#dbeafe",
                    color: "#1e40af",
                    fontWeight: 700,
                    "& .MuiChip-icon": { color: "#1e40af" },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    mb: 0.8,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.2rem" },
                  }}
                >
                  Try the Sandbox Account now
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#475569",
                    mb: 1.2,
                    maxWidth: 680,
                    lineHeight: 1.5,
                  }}
                >
                  Explore the full product experience with sample enterprise data before purchasing.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <Chip
                    variant="outlined"
                    label="User: john.demo@sushaservices.com"
                    sx={{ borderColor: "#bfdbfe", color: "#1e3a8a", bgcolor: "#ffffffcc" }}
                  />
                  <Chip
                    variant="outlined"
                    label="Password: Develop@123"
                    sx={{ borderColor: "#bfdbfe", color: "#1e3a8a", bgcolor: "#ffffffcc" }}
                  />
                </Box>
              </Box>

              <Box sx={{ flexShrink: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "stretch", md: "flex-end" },
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={openDemoExplorer}
                    startIcon={<LockOpen />}
                    endIcon={<Launch />}
                    sx={{
                      maxWidth: { xs: "100%", md: 250 },
                      fontWeight: 800,
                      borderRadius: 999,
                      px: 2.5,
                      py: 1.2,
                      bgcolor: "#2563eb",
                      "&:hover": { bgcolor: "#1d4ed8" },
                    }}
                  >
                    Launch Demo Portal
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Feature Comparison */}
        <Box sx={{ ...sectionMotion(160), textAlign: "center", mb: 4, width: '100%' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            All plans include
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Core features to help you manage your IT infrastructure
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 1.4, sm: 2.2, md: 3 }} sx={{ ...sectionMotion(230), mb: { xs: 3.2, sm: 4.1, md: 6 }, maxWidth: '1400px', mx: 'auto' }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center", p: 2 }}>
              <DevicesOther sx={{ fontSize: 48, color: "#6366f1", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Device Monitoring
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track all your devices in real-time with our intelligent agent
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center", p: 2 }}>
              <Cloud sx={{ fontSize: 48, color: "#059669", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Cloud Integration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect with Microsoft 365, Azure, and more
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center", p: 2 }}>
              <Analytics sx={{ fontSize: 48, color: "#2563eb", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Advanced Analytics
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get insights with powerful dashboards and reports
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center", p: 2 }}>
              <Security sx={{ fontSize: 48, color: "#7c3aed", mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Enterprise Security
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bank-level encryption and data protection
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Contact Sales */}
        <Paper
          sx={{
            ...sectionMotion(300),
            p: { xs: 2.2, sm: 3.1, md: 4 },
            textAlign: "center",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            borderRadius: 3,
            maxWidth: '1400px',
            mx: 'auto',
            width: '100%',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Need help choosing a plan?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.95 }}>
            Our team is here to help you find the perfect solution for your
            organization.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href="mailto:sales@sushaservices.com"
            sx={{
              bgcolor: "white",
              color: "#667eea",
              fontWeight: 700,
              px: 4,
              py: 1.5,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.9)",
              },
            }}
          >
            Contact Sales
          </Button>
        </Paper>
      </Container>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialog}
        onClose={() => !loading && setPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Complete Your Purchase
            </Typography>
            <Button
              size="small"
              onClick={() => setPaymentDialog(false)}
              disabled={loading}
            >
              <Close />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {enterpriseInfo && selectedPlan && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Enterprise Details
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: "#f8fafc" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Name:</strong> {enterpriseInfo.name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Email:</strong>{" "}
                  {enterpriseInfo.email || enterpriseInfo.poc_email}
                </Typography>
                <Typography variant="body2">
                  <strong>Contact:</strong> {enterpriseInfo.poc_phone || "N/A"}
                </Typography>
              </Paper>

              <TextField
                fullWidth
                label="GSTIN No. (Optional)"
                value={gstin}
                onChange={(e) => setGstin(e.target.value)}
                placeholder="22AAAAA0000A1Z5"
                sx={{ mb: 3 }}
                disabled={loading}
              />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Price Breakdown
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                {getPriceBreakdown() && (
                  <Box>
                    {getPriceBreakdown().isProrated && getPriceBreakdown().daysRemaining > 0 && (
                      <>
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#e0f2fe', borderRadius: 1 }}>
                          <Typography variant="caption" color="#0369a1" fontWeight={600}>
                            💡 Prorated Billing Applied
                          </Typography>
                          <Typography variant="caption" display="block" color="#0369a1" sx={{ mt: 0.5 }}>
                            You have {getPriceBreakdown().daysRemaining} days remaining on your current plan. We'll credit the unused portion.
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {plans[selectedPlan].name} Plan ({billingCycle})
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            ₹{getPriceBreakdown().newPlanPrice.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="success.main">
                            Unused credit ({getPriceBreakdown().daysRemaining} days)
                          </Typography>
                          <Typography variant="body2" color="success.main" fontWeight={600}>
                            -₹{getPriceBreakdown().unusedCredit.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            Prorated Amount
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            ₹{getPriceBreakdown().proratedAmount.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                      </>
                    )}
                    {(!getPriceBreakdown().isProrated || getPriceBreakdown().daysRemaining === 0) && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {plans[selectedPlan].name} Plan ({billingCycle})
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          ₹{getPriceBreakdown().proratedAmount.toLocaleString("en-IN")}
                        </Typography>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        CGST (2.5%)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ₹{getPriceBreakdown().cgst.toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        SGST (2.5%)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ₹{getPriceBreakdown().sgst.toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        ₹{getPriceBreakdown().total.toLocaleString("en-IN")}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPaymentDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePayNow}
            disabled={loading}
            sx={{
              px: 4,
              py: 1,
              bgcolor: selectedPlan ? plans[selectedPlan].color : "primary",
              "&:hover": {
                bgcolor: selectedPlan ? plans[selectedPlan].color : "primary",
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Pay Now"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        {showConfetti && (
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={500}
          />
        )}
        <DialogContent sx={{ textAlign: "center", py: 5 }}>
          <Celebration sx={{ fontSize: 80, color: "#059669", mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Congratulations! 🎉
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Your plan has been upgraded successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            You now have access to all the premium features. Enjoy your enhanced
            experience!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Redirecting to dashboard in {countdown} seconds...
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessDialog(false);
              navigate(ROUTES.DASHBOARD);
            }}
            sx={{ px: 4 }}
          >
            Go to Dashboard ({countdown})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
